import {
  Button,
  Chip,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
} from "@heroui/react";
import { AlertTriangle, Building2, FileSpreadsheet, Split } from "lucide-react";
import { useEffect, useState } from "react";

import FileDropZone from "@/components/common/FileDropZone";
import type { DesignatedVendor } from "@/features/partners/api";
import { errorMessage } from "@/lib/api";
import { importQuotation, type Quotation, type QuotationImportResult } from "./api";

/** プレビューに出す明細の最大行数（多い依頼で画面が流れ続けるのを防ぐ）。 */
const PREVIEW_ROWS = 50;

/**
 * 業者ごとの依頼ファイルの形式。API 側の対応表（quotation_import_service）と
 * 揃えてある。京セラは見積回答 CSV、平田機工は EDI の Excel で届く。
 *
 * 対応表を持たない業者（新東工業・湖北精工）はここに無い。その業者を選ぶと
 * API が 400 を返すので、ここでは形式を絞らずに受け取って案内はサーバに任せる。
 */
const VENDOR_FILE: Partial<
  Record<DesignatedVendor, { accept: string; label: string }>
> = {
  kyocera: { accept: ".csv", label: "CSV" },
  hirata: { accept: ".xlsx", label: "Excel" },
};

/**
 * 見積依頼ファイルの取り込みダイアログ。
 *
 * **下見 → 確認 → 取り込み**の 2 段にしてある。客先ファイルの体裁は運用で変わる
 * ので、いきなり起票すると間違った見積を作っては消すことになる。下見と取り込みは
 * サーバ側で同じ解析を通るため、ここで見えた内容がそのまま起票される。
 *
 * **得意先は選ばせない**。取引先マスタの「指定業者」から決まる。取り込んだ図面には
 * その得意先が入るので（[[drawings]]）、取り込みのたびに選ばせると取り違えて別の客先の
 * 図面に紐付く。どの得意先に入るかは下見の結果に出す。
 *
 * **1 ファイルに依頼番号が 2 つ以上あった場合はそこで止める**。1 依頼のつもりで
 * 出されたファイルに番号違いが混ざっているなら原本の誤りの可能性があり、どちらに
 * 寄せても取り返しが面倒になる。原本を直すか、番号ごとに分けて登録するかを
 * 選んでもらう（サーバ側も split 無しでは 400 を返す）。
 */
export default function QuotationImportDialog({
  isOpen,
  vendor,
  formatLabel,
  onClose,
  onImported,
}: {
  isOpen: boolean;
  /** EDI メニューで選ばれた指定業者。 */
  vendor: DesignatedVendor | null;
  formatLabel: string;
  onClose: () => void;
  /** 起票された見積。分割して取り込んだ場合は依頼番号ごとに並ぶ。 */
  onImported: (quotations: Quotation[]) => void;
}) {
  const [file, setFile] = useState<File | null>(null);
  const [preview, setPreview] = useState<QuotationImportResult | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  // 開くたびに初期化する（前回の下見結果を持ち越さない）。
  useEffect(() => {
    if (!isOpen) return;
    setFile(null);
    setPreview(null);
    setError(null);
    setBusy(false);
  }, [isOpen, vendor]);

  const run = async (dryRun: boolean, split = false) => {
    if (!file || !vendor) {
      setError("ファイルを選んでください。");
      return;
    }
    setBusy(true);
    setError(null);
    try {
      const result = await importQuotation({ file, vendor, dryRun, split });
      if (dryRun) {
        setPreview(result);
      } else if (result.quotations.length > 0) {
        onImported(result.quotations);
        onClose();
      }
    } catch (e) {
      setError(errorMessage(e, "取り込みに失敗しました。"));
    } finally {
      setBusy(false);
    }
  };

  // 受け付ける形式は業者で決まる（中身からは判別しない）。
  const format = vendor ? VENDOR_FILE[vendor] : undefined;
  const extension = format?.accept ?? ".csv,.xlsx";

  const shownItems = preview?.items.slice(0, PREVIEW_ROWS) ?? [];
  const groups = preview?.groups ?? [];
  // 依頼が 2 件以上あるファイル。そのままでは取り込めない。
  const needsSplit = groups.length > 1;
  const single = groups.length === 1 ? groups[0] : null;

  return (
    <Modal
      isOpen={isOpen}
      onOpenChange={(open) => !open && onClose()}
      size="4xl"
      scrollBehavior="inside"
    >
      <ModalContent>
        <ModalHeader className="flex-col items-start gap-1">
          <span>{formatLabel}の見積依頼を取り込む</span>
          <span className="text-small font-normal text-default-500">
            {`客先から届いた${format ? ` ${format.label} ファイル` : "ファイル"}をそのまま選んでください。先に内容を確認してから取り込みます。得意先は取引先マスタの「指定業者」から決まるので、ここでは選びません。`}
          </span>
        </ModalHeader>

        <ModalBody>
          <FileDropZone
            label="依頼ファイル"
            accept={extension}
            hint={`客先から届いた ${extension} をそのまま入れてください`}
            file={file}
            isDisabled={busy}
            onSelect={(selected) => {
              setFile(selected);
              // ファイルを入れ替えたら、前の下見結果は捨てる。
              setPreview(null);
              setError(null);
            }}
          />

          {preview && (
            <div className="mt-2 flex flex-col gap-3">
              <div className="flex flex-wrap items-center gap-2 rounded-md bg-default-50 p-3 text-sm">
                <Building2 size={16} className="text-brand-500" />
                <span className="font-bold">{preview.client_name}</span>
                <span className="text-default-500">に</span>
                <FileSpreadsheet size={16} className="text-brand-500" />
                <span className="font-bold">{preview.item_count} 行</span>
                <span className="text-default-500">
                  {needsSplit
                    ? `を ${groups.length} 件の見積に分けて取り込みます`
                    : "を取り込みます"}
                </span>
                {single?.client_request_number && (
                  <Chip size="sm" variant="flat">
                    依頼番号 {single.client_request_number}
                  </Chip>
                )}
                {single?.header.subject && (
                  <Chip size="sm" variant="flat">
                    {single.header.subject}
                  </Chip>
                )}
                {single?.header.answer_due_date && (
                  <Chip size="sm" variant="flat" color="primary">
                    回答期限 {single.header.answer_due_date}
                  </Chip>
                )}
                {single?.header.is_urgent && (
                  <Chip size="sm" variant="flat" color="danger">
                    特急
                  </Chip>
                )}
              </div>

              {/* 依頼が 2 件以上あるファイル。原本の誤りの可能性があるので、
                  そのまま取り込ませず、どうするかをここで選んでもらう。 */}
              {needsSplit && (
                <div
                  className="rounded-md bg-warning-50 p-3 text-sm text-warning-700"
                  data-testid="split-notice"
                >
                  <p className="mb-1 flex items-center gap-2 font-bold">
                    <AlertTriangle size={16} />
                    依頼番号が {groups.length} 件入っています
                  </p>
                  <p className="mb-2">
                    1
                    件の依頼ではない可能性があります。原本を直して取り込み直すか、依頼番号ごとに分けて登録してください。
                  </p>
                  <ul className="list-disc pl-5">
                    {groups.map((group, index) => (
                      <li key={group.client_request_number ?? index}>
                        <span className="font-bold">
                          {group.client_request_number ?? "依頼番号なし"}
                        </span>
                        {" … "}
                        {group.item_count} 行
                        {group.header.subject && `（${group.header.subject}）`}
                        {group.existing_quotation_numbers.length > 0 &&
                          ` ※同じ依頼番号の見積が既にあります（${group.existing_quotation_numbers.join("、")}）`}
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {single && single.existing_quotation_numbers.length > 0 && (
                <p className="flex items-start gap-2 rounded-md bg-warning-50 p-3 text-sm text-warning-700">
                  <AlertTriangle size={16} className="mt-0.5 shrink-0" />
                  <span>
                    同じ客先依頼番号の見積が既にあります（
                    {single.existing_quotation_numbers.join("、")}
                    ）。二重取り込みでなければそのまま進めてください。
                  </span>
                </p>
              )}

              {preview.warnings.length > 0 && (
                <div className="rounded-md bg-warning-50 p-3 text-sm text-warning-700">
                  <p className="mb-1 flex items-center gap-2 font-bold">
                    <AlertTriangle size={16} />
                    気づいた点（{preview.warnings.length} 件）
                  </p>
                  <ul className="list-disc pl-5">
                    {preview.warnings.map((warning, index) => (
                      <li key={index}>
                        {warning.row != null && `${warning.row} 行目: `}
                        {warning.message}
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              <div className="max-h-80 overflow-auto rounded-md border border-default-200">
                <table className="w-full text-sm">
                  <thead className="sticky top-0 bg-default-100 text-left">
                    <tr>
                      <th className="px-2 py-1.5 font-bold">行</th>
                      {/* どの依頼に入るかは分けるときだけ要る。 */}
                      {needsSplit && (
                        <th className="px-2 py-1.5 font-bold">依頼番号</th>
                      )}
                      <th className="px-2 py-1.5 font-bold">図番</th>
                      <th className="px-2 py-1.5 font-bold">品名</th>
                      <th className="px-2 py-1.5 text-right font-bold">数量</th>
                      <th className="px-2 py-1.5 font-bold">単位</th>
                      <th className="px-2 py-1.5 font-bold">希望納期</th>
                    </tr>
                  </thead>
                  <tbody>
                    {shownItems.map((item) => (
                      <tr key={item.row} className="border-t border-default-100">
                        <td className="px-2 py-1.5 text-default-500">{item.row}</td>
                        {needsSplit && (
                          <td className="px-2 py-1.5">
                            {item.client_request_number ?? "—"}
                          </td>
                        )}
                        {/* 図番はファイルに書かれていたまま取り込む（末尾の
                            記号を版として切り出したりはしない）。 */}
                        <td className="px-2 py-1.5">{item.drawing_number ?? "—"}</td>
                        <td className="px-2 py-1.5">{item.item_name ?? "—"}</td>
                        <td className="px-2 py-1.5 text-right">{item.quantity}</td>
                        <td className="px-2 py-1.5">{item.unit ?? "—"}</td>
                        <td className="px-2 py-1.5">
                          {item.requested_deadline ?? "—"}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {preview.items.length > shownItems.length && (
                  <p className="border-t border-default-100 px-2 py-1.5 text-center text-default-500">
                    ほか {preview.items.length - shownItems.length} 行
                  </p>
                )}
              </div>
            </div>
          )}

          {error && <p className="text-sm text-danger">{error}</p>}
        </ModalBody>

        <ModalFooter>
          <Button variant="light" radius="full" data-testid="cancel" onPress={onClose}>
            {needsSplit ? "原本を修正する" : "キャンセル"}
          </Button>
          {preview && needsSplit ? (
            <Button
              color="warning"
              radius="full"
              data-testid="import-split"
              startContent={<Split size={18} />}
              isLoading={busy}
              onPress={() => void run(false, true)}
            >
              {groups.length} 件に分けて登録する
            </Button>
          ) : preview ? (
            <Button
              color="primary"
              radius="full"
              data-testid="import-submit"
              isLoading={busy}
              onPress={() => void run(false)}
            >
              この内容で取り込む
            </Button>
          ) : (
            <Button
              color="primary"
              radius="full"
              data-testid="import-preview"
              isDisabled={!file}
              isLoading={busy}
              onPress={() => void run(true)}
            >
              内容を確認
            </Button>
          )}
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}
